import { KlaviyoCustomer } from "./types";
import { absoluteUrl, parsePriceToNumeric } from "./utils";
import { ensureKlaviyoQueue } from "./script";

const canTrack = () =>
  typeof window !== "undefined" && window.vfConsent?.has("marketing") === true;

let pendingEvents: Array<[string, any]> = [];
let consentEpoch = 0;
export const clearPendingKlaviyoEvents = () => {
  pendingEvents = [];
  consentEpoch++;
};

export const pushTrack = (event: string, properties: any) => {
  if (!canTrack()) return;
  ensureKlaviyoQueue();
  const w = window as any;
  if (!w.klaviyo) return;
  if (!event) return;
  const epoch = consentEpoch;
  w.klaviyo.push([
    "isIdentified",
    (identified: boolean) => {
      if (epoch !== consentEpoch) return;
      if (!canTrack()) {
        clearPendingKlaviyoEvents();
        return;
      }
      if (identified) w.klaviyo.push(["track", event, properties]);
      else
        pendingEvents = [
          ...pendingEvents,
          [event, properties] as [string, any],
        ].slice(-50);
    },
  ]);
};

export const pushIdentify = (customer: KlaviyoCustomer) => {
  if (!canTrack()) return;
  ensureKlaviyoQueue();
  const w = window as any;
  if (!w.klaviyo || !customer || !customer.email) return;
  const identity: Record<string, any> = {
    email: customer.email,
    first_name: customer.firstName || "",
    last_name: customer.lastName || "",
  };
  if (customer.phone) identity.phone_number = customer.phone;
  if (customer.languageId) identity.language_id = customer.languageId;
  const epoch = consentEpoch;
  w.klaviyo.push([
    "identify",
    identity,
    () => {
      if (epoch !== consentEpoch) return;
      if (!canTrack()) {
        clearPendingKlaviyoEvents();
        return;
      }
      const queued = pendingEvents;
      pendingEvents = [];
      for (const [event, properties] of queued)
        w.klaviyo.push(["track", event, properties]);
    },
  ]);
};

/**
 * Build & push the "Viewed Product" event entirely on the client from the
 * already-loaded Vuex product. This avoids a per-PDP roundtrip to the
 * OpenCart server. Categories are intentionally omitted: they are not
 * exposed on the d_vuefront Product type and Klaviyo can pull them from
 * the catalog sync (oc_klaviyo_catalog) for segmentation / Browse Abandon.
 */
export const trackViewedProduct = (
  storefrontUrl: string | undefined,
  product: any
) => {
  if (!canTrack()) return;
  const id = String(product.id);

  const numericPrice = parsePriceToNumeric(
    product.special && String(product.special).trim() !== ""
      ? product.special
      : product.price
  );
  const productUrl = absoluteUrl(storefrontUrl, `/store/product/${id}`);
  const title = String(product.name || "");
  const brand = product.manufacturer?.name
    ? String(product.manufacturer.name)
    : "";
  const sku = product.sku ? String(product.sku) : "";
  const imageUrl = product.imageBig || product.image || product.imageLazy || "";

  const properties: Record<string, any> = {
    ProductName: title,
    ProductID: id,
    SKU: sku,
    Categories: [],
    ImageURL: imageUrl ? absoluteUrl(storefrontUrl, imageUrl) : "",
    URL: productUrl,
    Brand: brand,
    Price: numericPrice,
    CompareAtPrice: parsePriceToNumeric(product.price),
  };

  pushTrack("Viewed Product", properties);

  if (typeof window !== "undefined") {
    const w = window as any;
    if (w.klaviyo) {
      w.klaviyo.push([
        "trackViewedItem",
        {
          Title: title,
          ItemId: id,
          Categories: [],
          ImageUrl: properties.ImageURL,
          Url: productUrl,
          Metadata: {
            Brand: brand,
            Price: numericPrice,
            CompareAtPrice: properties.CompareAtPrice,
          },
        },
      ]);
    }
  }
};

/**
 * Build & push "Viewed Category" entirely on the client. The SPA already
 * has all the fields we need from store/category/get, so no GraphQL hop.
 */
export const trackViewedCategory = (
  storefrontUrl: string | undefined,
  category: any
) => {
  const id = String(category.id);

  const properties = {
    CategoryName: String(category.name || ""),
    CategoryID: id,
    ImageURL: category.image ? absoluteUrl(storefrontUrl, category.image) : "",
    URL: absoluteUrl(storefrontUrl, `/store/category/${id}`),
  };

  pushTrack("Viewed Category", properties);
};
